import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, GripVertical } from "lucide-react";
import adminApi from "../lib/adminApi";
import { AdminImg } from "../components/AdminImg";
import { ShopAdminGate } from "../components/ShopAdminGate";
import { adminBase, adminHomeLabel } from "../lib/adminBase";

const COLUMNS = [
  { key: "queued", label: "Queued", accent: "bg-amber-100 text-amber-800" },
  { key: "printing", label: "Printing", accent: "bg-blue-100 text-blue-800" },
  { key: "printed", label: "Printed", accent: "bg-emerald-100 text-emerald-800" },
];

function QueueInner() {
  const [columns, setColumns] = useState(null);
  const [meta, setMeta] = useState(null);
  const [showAllPrinted, setShowAllPrinted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [drag, setDrag] = useState(null); // { duckId, orderId, height, from }
  const [over, setOver] = useState(null);
  const busyRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/queue", { params: { show_all_printed: showAllPrinted || undefined } });
      setColumns(data.columns);
      setMeta({ counts: data.counts, printedWindowDays: data.printed_window_days, printedTotal: data.printed_total });
    } catch {
      setColumns(null);
    } finally {
      setLoading(false);
    }
  }, [showAllPrinted]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    busyRef.current = !!drag;
  }, [drag]);

  useEffect(() => {
    // Auto-refresh the board every 15 minutes while staff have it open (skipped mid-drag). Only runs inside the admin session.
    const id = setInterval(() => {
      if (!busyRef.current) load();
    }, 15 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  const drop = async (toStatus) => {
    setOver(null);
    const d = drag;
    setDrag(null);
    if (!d || d.from === toStatus) return;
    setColumns((cols) => {
      const card = (cols[d.from] || []).find((c) => c.duck_id === d.duckId && String(c.order_id) === d.orderId && (c.height ?? null) === (d.height ?? null));
      if (!card) return cols;
      return {
        ...cols,
        [d.from]: cols[d.from].filter((c) => !(c.duck_id === d.duckId && String(c.order_id) === d.orderId && (c.height ?? null) === (d.height ?? null))),
        [toStatus]: [{ ...card, print_status: toStatus }, ...cols[toStatus]],
      };
    });
    try {
      await adminApi.post(`/orders/${d.orderId}/ducks/${d.duckId}/print-status`, { status: toStatus }, { params: d.height ? { height: d.height } : {} });
    } catch {
      load();
    }
  };

  return (
    <div data-testid="shop-admin-queue-page" className="min-h-screen bg-dots">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <Link to={adminBase()} data-testid="queue-back-link" className="text-sm text-accent font-semibold hover:underline">{adminHomeLabel()}</Link>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold mt-1">Print queue</h1>
            <p className="text-xs text-muted-foreground mt-1">Drag a duck between columns to update its print status. Shipping stays in Shopify.</p>
          </div>
          <Link to={`${adminBase()}/orders`} data-testid="queue-to-orders" className="text-sm text-accent font-semibold hover:underline">All orders →</Link>
        </div>

        {loading ? (
          <div className="py-20 grid place-items-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !columns ? (
          <div data-testid="queue-error" className="py-20 text-center text-rose-600">Could not load the print queue.</div>
        ) : (
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {COLUMNS.map((col) => (
              <div
                key={col.key}
                data-testid={`queue-col-${col.key}`}
                onDragOver={(e) => { e.preventDefault(); setOver(col.key); }}
                onDragLeave={() => setOver((o) => (o === col.key ? null : o))}
                onDrop={() => drop(col.key)}
                className={`rounded-3xl border p-3 min-h-[60vh] transition-colors ${over === col.key ? "border-accent bg-accent/5" : "border-border bg-card/50"}`}
              >
                <div className="flex items-center justify-between px-1 pb-3">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${col.accent}`}>{col.label}</span>
                  <span data-testid={`queue-count-${col.key}`} className="text-xs text-muted-foreground font-mono2">
                    {col.key === "printed" && meta?.printedWindowDays
                      ? `${columns.printed.length} of ${meta.printedTotal ?? columns.printed.length}`
                      : columns[col.key].length}
                  </span>
                </div>
                {col.key === "printed" && (
                  <div className="px-1 pb-2 flex items-center justify-between gap-2">
                    <span data-testid="queue-printed-window" className="text-[11px] text-muted-foreground">
                      {meta?.printedWindowDays ? `Last ${meta.printedWindowDays} days` : "All printed ducks"}
                    </span>
                    <button
                      type="button"
                      data-testid="queue-toggle-printed-window"
                      onClick={() => setShowAllPrinted((v) => !v)}
                      className="text-[11px] font-semibold text-accent hover:underline"
                    >
                      {showAllPrinted ? "Show last 14 days" : "Show all printed"}
                    </button>
                  </div>
                )}
                <div className="space-y-2">
                  {columns[col.key].map((c) => (
                    <div
                      key={`${c.order_id}-${c.duck_id}-${c.height ?? ""}`}
                      draggable
                      onDragStart={() => setDrag({ duckId: c.duck_id, orderId: String(c.order_id), height: c.height ?? null, from: col.key })}
                      onDragEnd={() => { setDrag(null); setOver(null); }}
                      data-testid={`queue-card-${c.duck_id}`}
                      className="rounded-2xl border border-border bg-card p-2.5 flex gap-2.5 items-center cursor-grab active:cursor-grabbing hover:shadow-sm"
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                      <AdminImg src={c.thumb} fallback={c.public_thumb} alt={c.duck_name || "duck"} className="h-11 w-11 rounded-lg object-cover bg-muted shrink-0" />
                      <div className="min-w-0">
                        <Link to={`${adminBase()}/orders/${c.order_id}`} className="text-sm font-bold truncate hover:underline block">{c.duck_name || "Duck"}</Link>
                        <p className="text-xs text-muted-foreground truncate">{c.order_name} · {c.height || "?"}mm{c.quantity > 1 ? ` · ×${c.quantity}` : ""}</p>
                        <p className="text-[11px] text-muted-foreground/80 truncate">{c.customer}</p>
                        {c.print_status_auto && (
                          <p data-testid={`queue-auto-${c.duck_id}`} className="text-[11px] text-emerald-700 font-semibold truncate">Printed (auto · Shopify shipped)</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {columns[col.key].length === 0 && (
                    <p className="text-center text-xs text-muted-foreground py-8">Drop ducks here</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default function ShopAdminQueue() {
  return (
    <ShopAdminGate>
      <QueueInner />
    </ShopAdminGate>
  );
}
